import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { overlayClose } from "../../../../shared/utils/overlayClose";
import { ClasificacionInput } from "./ClasificacionInput";

const OVERLAY = {
  position: "fixed", inset: 0, zIndex: 1000,
  background: "rgba(0,0,0,0.65)", backdropFilter: "blur(4px)",
  display: "flex", alignItems: "center", justifyContent: "center",
};

/**
 * CuentaModal — modal para crear o editar una cuenta contable.
 *
 * Props:
 *   mode    → "create" | "edit"
 *   padre   → objeto CuentaContable (padre bajo el que se crea) — solo en create
 *   cuenta  → objeto CuentaContable a editar — solo en edit
 *   onClose → cerrar el modal
 *   onSave  → async fn(data) → llamar al hook
 *   clasificacionesExistentes → string[] — sugerencias para el combo de
 *             clasificación (las ya usadas en el catálogo del condominio)
 */
export function CuentaModal({ mode, padre, cuenta, onClose, onSave, clasificacionesExistentes = [] }) {
  const { t } = useTranslation();
  const isEdit = mode === "edit";

  const [form,   setForm]   = useState({
    nombre:      isEdit ? cuenta?.nombre      : "",
    nombre_en:   isEdit ? (cuenta?.nombre_en ?? "") : "",
    descripcion: isEdit ? cuenta?.descripcion : "",
    is_detalle:  isEdit ? cuenta?.is_detalle  : false,
    es_costo_venta: isEdit ? (cuenta?.es_costo_venta ?? false) : false,
    es_recuperable: isEdit ? (cuenta?.es_recuperable ?? false) : false,
    clasificacion:  isEdit ? (cuenta?.clasificacion ?? "") : "",
    categoria_niif18: isEdit ? (cuenta?.categoria_niif18 ?? "") : "",
    padre:       padre?.id ?? null,
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [apiErr, setApiErr] = useState("");

  const nivelNuevo   = padre ? padre.nivel + 1 : null;
  const esNivel4     = nivelNuevo === 4;
  const puedeDetalle = isEdit
    ? !cuenta?.subcuentas?.length && cuenta?.nivel > 1
    : true;
  const tipoCuenta   = isEdit ? cuenta?.tipo : padre?.tipo;
  const esGasto      = tipoCuenta === "GASTO";
  const esIngresoOGasto = tipoCuenta === "GASTO" || tipoCuenta === "INGRESO";

  useEffect(() => {
    if (esNivel4) setForm((p) => ({ ...p, is_detalle: true }));
  }, [esNivel4]);

  function validate() {
    const e = {};
    if (!form.nombre.trim()) e.nombre = t("catalogo.nombreObligatorio");
    if (form.nombre.length > 150) e.nombre = t("catalogo.nombreMaximo");
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function handleSave(ev) {
    ev.preventDefault();
    if (!validate()) return;
    setSaving(true);
    setApiErr("");
    try {
      await onSave(isEdit ? { ...form } : { ...form, padre: padre.id });
      onClose();
    } catch (e) {
      setApiErr(e.message || "Error desconocido");
    } finally {
      setSaving(false);
    }
  }

  const tituloAccion = isEdit ? t("catalogo.editarCuenta") : t("catalogo.nuevaSubcuenta");
  const subtitulo = isEdit
    ? `${cuenta?.codigo} — ${cuenta?.nombre}`
    : `${t("catalogo.bajo")}: ${padre?.codigo} — ${padre?.nombre}`;

  const inpStyle = (err) => ({
    width: "100%", padding: "9px 12px",
    background: "var(--bg-input)",
    border: `1px solid ${err ? "#ef4444" : "var(--border-input)"}`,
    borderRadius: 7, color: "var(--text-primary)", fontSize: "0.87rem",
    outline: "none", boxSizing: "border-box", marginTop: 4,
  });

  const labelStyle = {
    display: "block", color: "var(--text-secondary)", fontSize: "0.72rem",
    fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px",
  };

  return (
    <div style={OVERLAY} {...overlayClose(onClose)}>
      <div style={{
        background: "var(--bg-card)", border: "1px solid var(--border-card)",
        borderRadius: 12, padding: 28, width: "min(480px, 94vw)",
        boxShadow: "0 32px 80px rgba(0,0,0,0.4)",
      }} onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{
              background: "rgba(249,115,22,0.15)", border: "1px solid rgba(249,115,22,0.3)",
              borderRadius: 8, padding: "7px 9px", color: "#f97316",
            }}>
              <i className={`fa-solid ${isEdit ? "fa-pen" : "fa-plus"}`} />
            </div>
            <div>
              <h3 style={{ margin: 0, color: "var(--text-primary)", fontSize: "1rem", fontWeight: 700 }}>
                {tituloAccion}
              </h3>
              <p style={{ margin: 0, color: "var(--text-muted)", fontSize: "0.75rem" }}>{subtitulo}</p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSave}>
          {/* Nombre */}
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>
              {t("catalogo.nombre")} <span style={{ color: "#f87171" }}>*</span>
            </label>
            <input
              style={inpStyle(errors.nombre)}
              value={form.nombre}
              onChange={(e) => setForm((p) => ({ ...p, nombre: e.target.value }))}
              placeholder="Ej: Efectivo en caja"
              autoFocus
            />
            {errors.nombre && (
              <div style={{ color: "#f87171", fontSize: "0.75rem", marginTop: 3 }}>
                {errors.nombre}
              </div>
            )}
          </div>

          {/* Nombre en inglés */}
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>
              {t("catalogo.nombreEn")} <span style={{ color: "var(--text-muted)", fontSize: "0.68rem", fontWeight: 400, textTransform: "none" }}>({t("common.opcional")})</span>
            </label>
            <input
              style={inpStyle(false)}
              value={form.nombre_en}
              onChange={(e) => setForm((p) => ({ ...p, nombre_en: e.target.value }))}
              placeholder={t("catalogo.nombreEnPlaceholder")}
            />
            <div style={{ color: "var(--text-muted)", fontSize: "0.72rem", marginTop: 3 }}>
              {t("catalogo.nombreEnDesc")}
            </div>
          </div>

          {/* Descripción */}
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>
              {t("common.descripcion")} ({t("common.opcional")})
            </label>
            <textarea
              style={{ ...inpStyle(false), resize: "vertical", minHeight: 64 }}
              value={form.descripcion}
              onChange={(e) => setForm((p) => ({ ...p, descripcion: e.target.value }))}
              placeholder="Descripción o notas contables..."
            />
          </div>

          {/* Is Detalle */}
          {puedeDetalle && (
            <div style={{
              marginBottom: 18, background: "rgba(249,115,22,0.06)",
              border: "1px solid rgba(249,115,22,0.2)", borderRadius: 8,
              padding: "12px 14px",
            }}>
              <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={form.is_detalle || esNivel4}
                  disabled={esNivel4}
                  onChange={(e) => setForm((p) => ({ ...p, is_detalle: e.target.checked }))}
                  style={{ accentColor: "#f97316", marginTop: 2 }}
                />
                <div>
                  <div style={{ color: "var(--text-primary)", fontSize: "0.85rem", fontWeight: 600 }}>
                    {t("catalogo.cuentaDetalle")}
                    {esNivel4 && (
                      <span style={{
                        marginLeft: 8, fontSize: "0.7rem", color: "#f97316",
                        background: "rgba(249,115,22,0.15)", padding: "1px 6px", borderRadius: 4,
                      }}>
                        {t("catalogo.nivel4Obligatorio")}
                      </span>
                    )}
                  </div>
                  <div style={{ color: "var(--text-muted)", fontSize: "0.75rem", marginTop: 2 }}>
                    {t("catalogo.cuentaDetalleDesc")}
                  </div>
                </div>
              </label>
            </div>
          )}

          {/* Es Costo de Ventas — solo cuentas de tipo GASTO */}
          {esGasto && (
            <div style={{
              marginBottom: 18, background: "rgba(249,115,22,0.06)",
              border: "1px solid rgba(249,115,22,0.2)", borderRadius: 8,
              padding: "12px 14px",
            }}>
              <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={form.es_costo_venta}
                  onChange={(e) => setForm((p) => ({ ...p, es_costo_venta: e.target.checked }))}
                  style={{ accentColor: "#f97316", marginTop: 2 }}
                />
                <div>
                  <div style={{ color: "var(--text-primary)", fontSize: "0.85rem", fontWeight: 600 }}>
                    {t("catalogo.esCostoVenta")}
                  </div>
                  <div style={{ color: "var(--text-muted)", fontSize: "0.75rem", marginTop: 2 }}>
                    {t("catalogo.esCostoVentaDesc")}
                  </div>
                </div>
              </label>
            </div>
          )}

          {/* Es Rubro Recuperable — cuentas de INGRESO o GASTO (facturados vía consumo de agua) */}
          {esIngresoOGasto && (
            <div style={{
              marginBottom: 18, background: "rgba(59,130,246,0.06)",
              border: "1px solid rgba(59,130,246,0.2)", borderRadius: 8,
              padding: "12px 14px",
            }}>
              <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={form.es_recuperable}
                  onChange={(e) => setForm((p) => ({ ...p, es_recuperable: e.target.checked }))}
                  style={{ accentColor: "#3b82f6", marginTop: 2 }}
                />
                <div>
                  <div style={{ color: "var(--text-primary)", fontSize: "0.85rem", fontWeight: 600 }}>
                    {t("catalogo.esRecuperable")}
                  </div>
                  <div style={{ color: "var(--text-muted)", fontSize: "0.75rem", marginTop: 2 }}>
                    {t("catalogo.esRecuperableDesc")}
                  </div>
                </div>
              </label>
            </div>
          )}

          {/* Categoría NIIF 18 — solo cuentas de INGRESO o GASTO */}
          {esIngresoOGasto && (
            <div style={{ marginBottom: 18 }}>
              <label style={labelStyle}>
                {t("catalogo.categoriaNiif18")}{" "}
                <span style={{ color: "var(--text-muted)", fontSize: "0.68rem", fontWeight: 400, textTransform: "none" }}>
                  ({t("common.opcional")})
                </span>
              </label>
              <select
                value={form.categoria_niif18}
                onChange={(e) => setForm((p) => ({ ...p, categoria_niif18: e.target.value }))}
                style={{ ...inpStyle(false), cursor: "pointer" }}
              >
                <option value="">{t("catalogo.categoriaNiif18Heredar")}</option>
                <option value="OPERACION">{t("catalogo.niif18Operacion")}</option>
                <option value="INVERSION">{t("catalogo.niif18Inversion")}</option>
                <option value="FINANCIAMIENTO">{t("catalogo.niif18Financiamiento")}</option>
                <option value="IMPUESTO">{t("catalogo.niif18Impuesto")}</option>
              </select>
              <div style={{ color: "var(--text-muted)", fontSize: "0.72rem", marginTop: 3 }}>
                {t("catalogo.categoriaNiif18Desc")}
              </div>
            </div>
          )}

          {/* Clasificación para reportes */}
          <div style={{ marginBottom: 18 }}>
            <label style={labelStyle}>
              {t("catalogo.clasificacion")}{" "}
              <span style={{ color: "var(--text-muted)", fontSize: "0.68rem", fontWeight: 400, textTransform: "none" }}>
                ({t("common.opcional")})
              </span>
            </label>
            <div style={{ marginTop: 4 }}>
              <ClasificacionInput
                value={form.clasificacion}
                onChange={(v) => setForm((p) => ({ ...p, clasificacion: v }))}
                options={clasificacionesExistentes}
                placeholder={t("catalogo.clasificacionPlaceholder")}
              />
            </div>
            <div style={{ color: "var(--text-muted)", fontSize: "0.72rem", marginTop: 3 }}>
              {t("catalogo.clasificacionDesc")}
            </div>
          </div>

          {/* Error de API */}
          {apiErr && (
            <div style={{
              background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.3)",
              borderRadius: 6, padding: "8px 12px", marginBottom: 14,
              color: "#f87171", fontSize: "0.8rem",
              display: "flex", alignItems: "center", gap: 6,
            }}>
              <i className="fa-solid fa-circle-exclamation" />
              {apiErr}
            </div>
          )}

          {/* Acciones */}
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button type="button" onClick={onClose}
              style={{
                padding: "8px 18px", background: "transparent",
                border: "1px solid var(--border-card)", borderRadius: 7,
                color: "var(--text-secondary)", cursor: "pointer", fontSize: "0.84rem",
              }}>
              {t("common.cancelar")}
            </button>
            <button type="submit" disabled={saving}
              style={{
                padding: "8px 20px", background: "#f97316",
                border: "none", borderRadius: 7,
                color: "#fff", fontWeight: 600,
                cursor: saving ? "not-allowed" : "pointer",
                fontSize: "0.84rem", opacity: saving ? 0.7 : 1,
              }}>
              {saving
                ? <><i className="fa-solid fa-circle-notch fa-spin" style={{ marginRight: 6 }} />{t("catalogo.guardando")}</>
                : isEdit ? t("catalogo.guardarCambios") : t("catalogo.crearCuenta")
              }
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
